/**
 * scenarioParamCells.js
 *
 * 시나리오 파라미터 편집기(IP/RP/MP/DP/BF) 공용 — 접힌 행의 값 표시.
 *
 * 행은 3열로 읽힌다: `파라미터명 | 적용되는 값 | 현행 값`.
 *   - 적용되는 값 : 조정했으면 그 값(accent 볼드), 손대지 않았으면 "현행 값 유지"(회색).
 *                   미조정 행에 현재값을 되비추지 않는 이유는 그 값의 단위가 파라미터 타입과
 *                   다를 수 있기 때문 — RP MOQ 는 타입이 배율(multiplier)인데 current_value 는
 *                   품목 EA 라, 되비추면 "35배"가 되어 현행이 35배라는 오해를 만든다.
 *   - 현행 값     : 지금 설정/실적 기준값(평균 등). 항상 회색 보조 텍스트.
 * 조정 여부와 무관하게 두 열이 늘 차 있어야 행 가운데가 비어 보이지 않는다 —
 * 조정된 행에만 값을 그리면 미조정 행이 라벨만 남아 리스트가 듬성해진다.
 *
 * (이전에는 `현행 95% → 97%` 취소선 diff 한 덩어리였다. 3열 정렬이 스캔하기 쉬워 폐기.)
 */
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { FONT } from "@plannel/components/ai/typographyTokens";
import { C } from "@plannel/components/ai/aiTokens";
import { hasEffectiveValue, isSignedParam, decToPct } from "./scenarioParamUtil";

// 파라미터 값 1개 → 표시 문자열. 유효값 없으면 null.
//   t          : 호출측 i18n 함수(모듈별 tr 래퍼도 동일 시그니처)
//   toggleText : toggle 타입 라벨러 (v) => string. 미지정 시 Y/N 그대로.
export function fmtParamValue(def, value, t, toggleText) {
  if (!hasEffectiveValue(def, value)) return null;
  const type = def?.type;
  if (type === "select") {
    const o = (def.options || []).find(x => String(x.value) === String(value));
    return o ? t(o.label_key || o.label) : String(value);
  }
  if (type === "toggle") {
    return toggleText ? toggleText(value) : String(value).toUpperCase();
  }
  if (type === "multiplier") return `${value}${t("multiplierUnitLabel")}`;
  // 배열 값(DP 거래처 계층·수식 등) — 코드 나열. 길면 행에서 잘린다(noWrap).
  if (Array.isArray(value)) return value.join(", ");
  const unit = def?.unit_key ? t(def.unit_key) : (def?.unit || "");
  // 증감 입력(IP 서비스 레벨 %p)은 부호가 의미의 절반 — 양수에도 + 를 붙여 상향임을 못 박는다.
  const n = Number(value);
  const sign = isSignedParam(def) && Number.isFinite(n) && n > 0 ? "+" : "";
  return `${sign}${value}${unit}`;
}

// 모듈 문구를 모르는 소비처(hover 미리보기·커스텀 저장 팝업)용 중립 토글 라벨러.
// 모듈마다 토글 문구가 다르므로(재고 주기 포함 / 캘린더 무시 등) 적용·미적용으로만 말한다.
const neutralToggleText = (t) => (v) =>
  t(String(v).toUpperCase() === "Y" ? "paramToggleOnLabel" : "paramToggleOffLabel");

// 파라미터 값 1개 → 표시 문자열. fmtParamValue 의 상위 집합이다.
// 편집기가 아니라 "값을 읽기만 하는" 소비처용이라, 카탈로그 meta 를 더 본다.
//   display="percent" : 저장 정본(소수·배수) → 표시 %(수율 0.97 은 97%)
//   레거시 def.format  : mod.params 만 있는 모듈의 배열·객체 표시 함수(BF 알고리즘·외부 인자)
//   mod.formatOverrideValue : 모듈 훅이 값을 주면 그것이 최우선(MP 잔업 하루 합계·계획 방식)
// fmtParamValue 자체에 percent 분기를 넣지 않는 이유는 편집기 호출부가 이미 표시 단위로
//   변환해 넘기기 때문이다 — 거기에 넣으면 이중 환산이 된다.
export function fmtParamDisplayValue(def, value, t, { toggleText, mod, sc, ov } = {}) {
  if (!hasEffectiveValue(def, value)) return null;
  if (typeof mod?.formatOverrideValue === "function") {
    const s = mod.formatOverrideValue(value, def, sc, ov);
    if (s != null && s !== "") return String(s);
  }
  if (typeof def?.format === "function") {
    const s = def.format(value, t);
    if (s != null && s !== "") return String(s);
  }
  // 선택지·토글이 percent 보다 먼저다 — 라벨이 있는 항목에 % 를 씌우면 값이 숫자로 뒤바뀐다.
  //   (MP fmtCur 와 커스텀 저장 팝업도 같은 순서를 쓴다. percent 는 수치·배율 항목에만 붙는다.)
  if (def?.display === "percent" && def?.type !== "select" && def?.type !== "toggle") {
    return `${decToPct(value)}%`;
  }
  return fmtParamValue(def, value, t, toggleText || neutralToggleText(t));
}

// 현행(기준) 값 1개 → 표시 문자열. 시나리오 값과 규칙이 달라 별도 함수다.
// 배율·토글 분기를 타지 않는 것이 핵심이다.
//   RP MOQ 는 편집 타입이 배율인데 현행값은 품목 평균 EA 다 — 배율로 읽으면 현행이 35배가 된다.
//   RP 발주 캘린더는 토글인데 현행값은 캘린더 적용 비율 %다 — 토글로 읽으면 "적용"이 된다.
// def.unit 은 백엔드 build_param_defs 가 baseline_unit 을 싣는 자리라 현행값 단위로 맞다.
export function fmtBaselineRawValue(def, raw, t, { mod, sc, ov } = {}) {
  if (raw == null || raw === "") return null;
  if (typeof mod?.formatOverrideValue === "function") {
    const s = mod.formatOverrideValue(raw, def, sc, ov);
    if (s != null && s !== "") return String(s);
  }
  if (def?.type === "select") {
    const o = (def.options || []).find(x => String(x.value) === String(raw));
    return o ? t(o.label_key || o.label) : String(raw);
  }
  const unit = def?.unit_key ? t(def.unit_key) : (def?.unit || "");
  return `${Number.isFinite(Number(raw)) ? Number(raw) : raw}${unit}`;
}

// 편집 종료 → 같은 클릭이 행을 다시 여는 것을 막는 가드.
//   행 헤더를 누르면 blur(편집 종료) 가 click 보다 먼저 오고, 그때 이미 닫힌 상태라
//   뒤이은 click 의 toggle 이 행을 도로 열어 버린다. 방금 닫혔으면 그 클릭 한 번을 무시한다.
export function useEditEndGuard() {
  const atRef = useRef(0);
  return {
    markEnd: () => { atRef.current = Date.now(); },
    justEnded: () => Date.now() - atRef.current < 250,
  };
}

// 현행 열 접두어 — 항목 성격에 따라 다르다.
//   1) 백엔드 baseline_desc_key 가 있으면 그 서술이 이미 성격을 말한다("평균 수송 LT 1.6일",
//      "캘린더 적용 비율 0%"). 참고치일 뿐 엔진에 그대로 들어가는 값도 아니므로 접두어를 안 붙인다.
//   2) 서술 없이 값만 오는 항목(IP 실적/수요계획 반영 구간)은 고정된 단일 현행값을 바꾸는 것이라
//      맥락이 필요하다 — 손대기 전에는 "현행 2개월", 바꾼 뒤에는 "변경 전 2개월".
//   3) 현재값 개념이 없는 항목(IP 서비스 레벨 = 증감, 목표재고 주기재고 반영 = 대상별 상이)은
//      current 자체가 비어 이 열이 통째로 빠진다.
export function currentPrefixFor(def, changed, t) {
  if (def?.baseline_desc_key) return null;
  if (def?.current_value == null || def.current_value === "") return null;
  return t(changed ? "paramBeforeChangeLabel" : "paramCurrentPrefixLabel");
}

// 현행 열 문자열 — 백엔드 baseline_kind 로 접두어만 붙이고 파라미터명은 재출력하지 않는다.
//   행의 좌측 라벨이 이미 이름을 말하므로, 파라미터명을 포함한 desc 전문은
//   가장 좁은 열에서 값까지 잘라먹었다.
//   current=현행 설정, avg=평균, custom=이름 재출력이 아닌 별도 지표(RP 캘린더 적용 비율)라 desc 전문 유지,
//   미제공=구버전 응답 → 기존 표시(desc 전문) 폴백.
//   조정 후에도 '현행 설정' 그대로 둔다 — 이 열은 늘 마스터의 현재 설정을 가리킨다.
//   valueText 는 모듈별 포맷터 결과(옵션 라벨·단위·% 환산은 호출측 책임).
export function baselineCurrentText(def, valueText, t) {
  if (valueText == null || valueText === "") return null;
  const kind = def?.baseline_kind;
  if (kind === "avg") return t("paramAvgValueLabel", { value: valueText });
  if (kind === "current") return t("paramCurrentValueLabel", { value: valueText });
  if (def?.baseline_desc_key) {
    return `${t(def.baseline_desc_key)}${def.type === "select" ? ": " : " "}${valueText}`;
  }
  return valueText;
}

// 잘린 행에서만 원문을 툴팁으로 — 행마다 뜨는 툴팁은 값 읽기를 방해하므로 넘칠 때만 붙인다.
// 세로 clamp(-webkit-line-clamp)는 scrollWidth 가 아니라 scrollHeight 로만 드러난다.
//   가로 넘침만 보면 여러 줄로 자른 카드 본문에는 title 이 아예 달리지 않는다.
//   +1 은 line-height 소수점 반올림으로 생기는 1px 오탐 방어다.
export function titleIfClipped(e) {
  const el = e.currentTarget;
  const clipped = el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight + 1;
  if (clipped) el.setAttribute("title", el.textContent);
  else el.removeAttribute("title");
}

// 접힌 상태에서도 조정을 취소할 수 있는 행 우측 아이콘 버튼.
//   헤더 전체가 펼침 토글이므로 stopPropagation 필수 — 안 하면 되돌리면서 행이 펼쳐진다.
//   hidden=true 면 자리만 차지한다. 미조정 행에서 이 버튼을 통째로 빼면 그 행만 우측
//   고정폭이 22px(아이콘+gap) 좁아지고, 값 열이 비율 flex 라 조정된 행과 정렬이 어긋난다.
//   title 은 hover 툴팁으로 띄우지 않는다 — 되돌리기 아이콘은 그 자체로 읽히고, 행마다 뜨는
//   툴팁이 값 읽기를 방해한다. 스크린리더용 aria-label 로만 남긴다.
export function ParamRevertButton({ onClick, disabled, title, accent, hidden }) {
  return (
    <Box component="button" type="button" disabled={disabled || hidden}
      aria-label={hidden ? undefined : title}
      aria-hidden={hidden ? "true" : undefined} tabIndex={hidden ? -1 : undefined}
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      sx={{ flex: "0 0 auto", width: 22, height: 22, p: 0, border: "none", borderRadius: "3px",
            display: "flex", alignItems: "center", justifyContent: "center",
            bgcolor: "transparent", color: C.text3,
            ...(hidden ? { visibility: "hidden", pointerEvents: "none" } : {}),
            cursor: disabled ? "not-allowed" : "pointer",
            transition: "color .12s, background-color .12s",
            "&:hover": disabled ? {} : { color: accent, bgcolor: `${accent}14` } }}>
      <RestartAltIcon sx={{ fontSize: 17 }} />
    </Box>
  );
}

// 행의 값 2열. 폭은 비율로 나눠 카드가 좁아져도(그리드 절반 폭) 열이 무너지지 않게 한다.
//   applied  : 조정된 값 문자열(없으면 null) — 있으면 강조, 없으면 "현행 값 유지".
//   input    : 편집 중일 때 값 자리에 놓을 입력 컨트롤(단위 접미 포함). 주면 텍스트 대신 이것을 그린다.
//   current  : 현행/평균 기준값 문자열(없으면 열이 비어도 무방 — 라벨·적용값이 이미 폭을 잡는다)
//   extras   : 값 옆에 붙는 노드(MP 적용 단위 칩 등). "이 값이 어디에 적용되는지"는 값의 일부라
//              값 셀 **안**에 둔다 — 값과 현행 사이에 끼우면 현행 열이 행마다 다른 위치로 밀린다.
export function ParamValueCells({ applied, current, currentPrefix, accent, extras, input, onEditEnd }) {
  const { t } = useTranslation();
  const changed = !!applied;
  const boxRef = useRef(null);
  const editing = !!input;

  // 행을 클릭해 편집으로 들어오면 곧바로 값을 칠 수 있어야 한다 — 입력까지 한 번 더 클릭시키지 않는다.
  useEffect(() => {
    if (!editing) return;
    const el = boxRef.current?.querySelector("input, select, textarea");
    if (!el) return;
    el.focus();
    if (el.tagName === "SELECT") {
      // 네이티브 select 는 포커스만으로 목록이 안 열린다. showPicker 는 사용자 제스처(행 클릭)
      //   컨텍스트에서만 허용되고 미지원 브라우저도 있으므로 실패는 무시한다(포커스는 이미 됨).
      try { el.showPicker?.(); } catch { /* 제스처 소실·미지원 — 클릭으로 열면 된다 */ }
    } else if (el.select && el.type !== "checkbox") {
      el.select();                                          // 숫자는 통째 선택 — 바로 덮어쓰기
    }
  }, [editing]);

  // 포커스가 이 셀 밖으로 나가면(다른 곳 클릭·Tab) 편집 종료. Enter/Esc 도 종료.
  //   한 행이 계속 입력 상태로 남아 있으면 목록이 읽기 어렵다.
  const endGuards = editing && onEditEnd ? {
    onBlur: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) onEditEnd(); },
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onEditEnd(); }
    },
  } : {};

  return (
    <>
      {/* 값 셀은 grow 하지 않는다 — 편집 중에 남는 공간을 다 먹으면 현행 열이 오른쪽 끝까지 밀린다.
          편집 중에는 select·단위 라벨이 34% 를 넘을 수 있어 minWidth 로 바닥만 잡고 확장을 허용한다. */}
      <Box ref={boxRef} {...endGuards}
        onClick={editing ? (e) => {
          // 컨트롤(입력·드롭다운·토글 버튼)을 직접 누른 경우에만 행 토글로 번지지 않게 막는다.
          //   셀의 남는 여백까지 막으면 "같은 행을 한 번 더 눌러 편집을 끝내는" 동작이 죽는다.
          if (e.target.closest("input, select, textarea, button, [role='button'], label")) e.stopPropagation();
        } : undefined}
        sx={{ ...(editing ? { flex: "0 1 auto", minWidth: "34%" } : { flex: "0 0 34%", minWidth: 0 }),
              display: "flex", alignItems: "center", gap: 0.4,
              // 입력 높이를 값 텍스트 줄높이에 맞춘다 — 그냥 두면 편집으로 들어갈 때 행이 몇 px
              //   높아졌다가 돌아와 목록이 출렁인다. 체크박스는 MUI 가 자체 크기를 쓰므로 제외.
              "& input:not([type='checkbox']), & select": {
                height: 22, py: 0, lineHeight: "20px", boxSizing: "border-box",
              } }}>
        {input || (
          <>
            <Typography noWrap component="span"
              sx={{ fontSize: FONT.BODY, minWidth: 0,
                    ...(changed ? { fontWeight: 700, color: accent } : { color: C.text3 }) }}>
              {changed ? applied : t("paramKeepCurrentShort")}
            </Typography>
            {extras}
          </>
        )}
      </Box>
      {/* 현행 열이 남는 공간을 흡수한다 — 텍스트는 좌측 정렬이라 편집 여부와 무관하게 자리가 고정된다. */}
      <Typography noWrap component="span" onMouseEnter={titleIfClipped}
        sx={{ fontSize: FONT.BODY, color: C.text3, flex: "1 1 auto", minWidth: 0 }}>
        {current ? (currentPrefix ? `${currentPrefix} ${current}` : current) : ""}
      </Typography>
    </>
  );
}
